import { useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useMe, useLogout } from "@/api/hooks";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationsBell } from "./NotificationsBell";

export function AppShell() {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const onPath = (p: string) => loc.pathname.startsWith(p);
  const closeMenu = () => setMenuOpen(false);

  const isOperatorPlus = me?.role === "operator" || me?.role === "admin";
  const isAdmin = me?.role === "admin";

  return (
    <div className="min-h-full flex flex-col bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="mx-auto max-w-7xl px-3 sm:px-4 py-3 flex items-center gap-3 sm:gap-6">
          <Link to="/" className="flex items-center gap-2 font-semibold whitespace-nowrap" onClick={closeMenu}>
            <img
              src="/panel/animus_mark.svg"
              alt=""
              className="h-14 w-14"
              aria-hidden="true"
            />
            <span>Animus Panel</span>
          </Link>

          {/* Desktop nav — hidden below md */}
          <nav className="hidden md:flex gap-4 text-sm">
            {isOperatorPlus && <NavLink to="/dashboard" current={onPath("/dashboard")}>Dashboard</NavLink>}
            <NavLink to="/receipts" current={onPath("/receipts")}>Receipts</NavLink>
            {isOperatorPlus && (
              <>
                <NavLink to="/packs" current={onPath("/packs")}>Packs</NavLink>
                <NavLink to="/fleet" current={onPath("/fleet")}>Fleet</NavLink>
                <NavLink to="/safety" current={onPath("/safety")}>Safety</NavLink>
                <NavLink to="/system" current={onPath("/system")}>System</NavLink>
              </>
            )}
            {isAdmin && <NavLink to="/users" current={onPath("/users")}>Users</NavLink>}
            {me && <NavLink to="/settings" current={onPath("/settings")}>Settings</NavLink>}
          </nav>

          <div className="ml-auto text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2 sm:gap-3">
            {me && <NotificationsBell />}
            <ThemeToggle />
            {/* Desktop user controls */}
            {me ? (
              <div className="hidden md:flex items-center gap-3">
                <span className="font-mono">{me.username}</span>
                <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs uppercase tracking-wide">
                  {me.role}
                </span>
                <button
                  className="hover:text-slate-900 dark:hover:text-white"
                  onClick={async () => { await logout.mutateAsync(); navigate("/login"); }}
                >Sign out</button>
              </div>
            ) : (
              <Link to="/login" className="hidden md:inline hover:text-slate-900 dark:hover:text-white">
                Sign in
              </Link>
            )}
            {/* Mobile menu toggle */}
            <button
              type="button"
              className="md:hidden rounded p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {menuOpen && (
          <div className="md:hidden border-t border-slate-200 dark:border-slate-700 px-3 py-2">
            <nav className="flex flex-col gap-1 text-sm">
              {isOperatorPlus && <MobileLink to="/dashboard" current={onPath("/dashboard")} onClick={closeMenu}>Dashboard</MobileLink>}
              <MobileLink to="/receipts" current={onPath("/receipts")} onClick={closeMenu}>Receipts</MobileLink>
              {isOperatorPlus && (
                <>
                  <MobileLink to="/packs" current={onPath("/packs")} onClick={closeMenu}>Packs</MobileLink>
                  <MobileLink to="/fleet" current={onPath("/fleet")} onClick={closeMenu}>Fleet</MobileLink>
                  <MobileLink to="/safety" current={onPath("/safety")} onClick={closeMenu}>Safety</MobileLink>
                  <MobileLink to="/system" current={onPath("/system")} onClick={closeMenu}>System</MobileLink>
                </>
              )}
              {isAdmin && <MobileLink to="/users" current={onPath("/users")} onClick={closeMenu}>Users</MobileLink>}
              {me && <MobileLink to="/settings" current={onPath("/settings")} onClick={closeMenu}>Settings</MobileLink>}
            </nav>
            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-sm">
              {me ? (
                <>
                  <div className="flex items-center gap-2 truncate">
                    <span className="font-mono truncate">{me.username}</span>
                    <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-xs uppercase tracking-wide flex-shrink-0">
                      {me.role}
                    </span>
                  </div>
                  <button
                    className="hover:text-slate-900 dark:hover:text-white flex-shrink-0"
                    onClick={async () => { closeMenu(); await logout.mutateAsync(); navigate("/login"); }}
                  >Sign out</button>
                </>
              ) : (
                <Link to="/login" className="hover:text-slate-900 dark:hover:text-white" onClick={closeMenu}>Sign in</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 mx-auto w-full max-w-7xl px-3 sm:px-4 py-4 sm:py-6">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400 px-3 sm:px-4 py-3 text-center">
        ARC Safety Authority — Animus control panel. Receipts verifiable offline
        with{" "}
        <code className="font-mono bg-slate-100 dark:bg-slate-700 px-1 rounded">
          python3 docs/patents/stage1/annexes/A_verify.py
        </code>
        .
      </footer>
    </div>
  );
}

function NavLink({
  to, current, children,
}: {
  to: string;
  current: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={
        "rounded px-2 py-1 transition-colors " +
        (current
          ? "bg-slate-900 dark:bg-purple-600 text-white"
          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700")
      }
    >
      {children}
    </Link>
  );
}

function MobileLink({
  to, current, onClick, children,
}: {
  to: string;
  current: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={
        "rounded px-3 py-2 transition-colors " +
        (current
          ? "bg-slate-900 dark:bg-purple-600 text-white"
          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700")
      }
    >
      {children}
    </Link>
  );
}
